import { useState, useEffect, useMemo } from 'react';
import { Megaphone, Bell, Home, Users, CalendarDays, Settings, X, Pin, Clock, CheckCircle2, Shield, Search, Filter, School } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import './responsavel-mobile.css';
import { ROUTES } from '../../api/routes';
import { useResponsavelId } from './useResponsavelId';
import { NotificacaoDetalheSheet } from '../../components/notifications/NotificacaoDetalheSheet';
import { ComunicadoVisualizacaoModal, type ComunicadoVisualizacaoData } from '../../components/comunicados/ComunicadoVisualizacaoModal';
import { UserAvatar } from '../../components/ui/UserAvatar';

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
}

interface Comunicado {
  id: string;
  titulo: string;
  corpo: string;
  tipo: string;
  destinatario: string;
  fixado: boolean;
  criadoEm: string;
  configPreview?: Record<string, string | boolean | number | null> | null;
  escola?: { id: string; nome: string; logoUrl?: string } | null;
  municipio?: { id: string; nome: string; logoUrl?: string; brasaoUrl?: string } | null;
}

function midiaUrlOf(c: Comunicado): string | null {
  const u = c.configPreview?.midiaUrl || (c as any).midiaUrl || (c as any).imagemUrl || (c as any).anexoUrl;
  return typeof u === 'string' && u.length > 0 ? u : null;
}

const TIPO_COLORS: Record<string, { bg: string; color: string }> = {
  AVISO:    { bg: '#fef3c7', color: '#d97706' },
  URGENTE:  { bg: '#fee2e2', color: '#dc2626' },
  EVENTO:   { bg: '#f3e8ff', color: '#7c3aed' },
  REUNIAO:  { bg: '#dcfce7', color: '#166534' },
  LEMBRETE: { bg: '#dbeafe', color: '#2563eb' },
};

export function ResponsavelComunicados() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const responsavelId = useResponsavelId();

  const [activeTab, setActiveTab] = useState<'mural' | 'avisos'>('mural');
  const [filterTipo, setFilterTipo] = useState<string>('TODOS');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [comunicados, setComunicados] = useState<Comunicado[]>([]);
  const [notificacoes, setNotificacoes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [selectedComunicado, setSelectedComunicado] = useState<Comunicado | null>(null);
  const [selectedNotificacao, setSelectedNotificacao] = useState<any | null>(null);

  const [readIds, setReadIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('ac_read_comunicados');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const handleSync = () => {
      try {
        const raw = localStorage.getItem('ac_read_comunicados');
        setReadIds(raw ? JSON.parse(raw) : []);
      } catch {}
    };
    window.addEventListener('ac:comunicado:read', handleSync);
    return () => window.removeEventListener('ac:comunicado:read', handleSync);
  }, []);

  const totalCount = comunicados.length;
  const lidosCount = comunicados.filter((c) => readIds.includes(c.id)).length;
  const pendentesCount = Math.max(0, totalCount - lidosCount);
  const percentLido = totalCount > 0 ? Math.round((lidosCount / totalCount) * 100) : 100;

  const contagemPorTipo = useMemo(() => {
    const counts: Record<string, number> = {
      AVISO: 0,
      URGENTE: 0,
      EVENTO: 0,
      REUNIAO: 0,
      LEMBRETE: 0,
    };
    comunicados.forEach((c) => {
      if (counts[c.tipo] !== undefined) counts[c.tipo]++;
      else counts[c.tipo] = 1;
    });
    return counts;
  }, [comunicados]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'avisos' || tabParam === 'mural') {
      setActiveTab(tabParam);
    }
    const notifId = searchParams.get('notificacaoId');
    if (notifId) {
      setActiveTab('avisos');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!responsavelId) return;
    let cancelled = false;
    const load = (silent = false) => {
      if (!silent) setLoading(true);
      api.get(ROUTES.mobileResponsavel.comunicados(responsavelId))
        .then((res) => { if (!cancelled) setComunicados(res.data || []); })
        .catch(() => { if (!cancelled) setComunicados([]); })
        .finally(() => { if (!cancelled && !silent) setLoading(false); });
    };
    load(false);
    const onPush = () => load(true);
    const onVis = () => { if (document.visibilityState === 'visible') load(true); };
    window.addEventListener('ac-push-received', onPush);
    window.addEventListener('focus', onPush);
    document.addEventListener('visibilitychange', onVis);
    const poll = window.setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, 15000);
    return () => {
      cancelled = true;
      window.removeEventListener('ac-push-received', onPush);
      window.removeEventListener('focus', onPush);
      document.removeEventListener('visibilitychange', onVis);
      window.clearInterval(poll);
    };
  }, [responsavelId]);

  useEffect(() => {
    if (!responsavelId) return;
    let cancelled = false;
    const loadN = (silent = false) => {
      if (!silent) setLoadingNotifs(true);
      api.get(ROUTES.mobileResponsavel.timeline(responsavelId, 50))
        .then((res) => { if (!cancelled) setNotificacoes(res.data || []); })
        .catch(() => { if (!cancelled) setNotificacoes([]); })
        .finally(() => { if (!cancelled && !silent) setLoadingNotifs(false); });
    };
    loadN(false);
    const onPush = () => loadN(true);
    const onVis = () => { if (document.visibilityState === 'visible') loadN(true); };
    window.addEventListener('ac-push-received', onPush);
    window.addEventListener('focus', onPush);
    document.addEventListener('visibilitychange', onVis);
    const poll = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadN(true);
    }, 15000);
    return () => {
      cancelled = true;
      window.removeEventListener('ac-push-received', onPush);
      window.removeEventListener('focus', onPush);
      document.removeEventListener('visibilitychange', onVis);
      window.clearInterval(poll);
    };
  }, [responsavelId]);

  // Deep-link check
  useEffect(() => {
    const notifId = searchParams.get('notificacaoId');
    if (notifId && notificacoes.length > 0) {
      const found = notificacoes.find((n) => n.id === notifId);
      if (found) {
        handleOpenNotificacao(found);
        const next = new URLSearchParams(searchParams);
        next.delete('notificacaoId');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, notificacoes]);

  const handleOpenNotificacao = (n: any) => {
    const comunicadoMatch = comunicados.find(
      (c) => c.id === n.comunicadoId || c.id === n.id || (n.titulo && c.titulo === n.titulo)
    );
    if (comunicadoMatch) {
      handleOpenComunicado(comunicadoMatch);
    } else {
      setSelectedNotificacao(n);
    }
    if (!n.lidaEm && responsavelId && n.id) {
      api.patch(ROUTES.mobileResponsavel.notificacaoLida(responsavelId, n.id)).catch(() => {});
      setNotificacoes((prev) =>
        prev.map((item) => (item.id === n.id ? { ...item, lidaEm: new Date().toISOString(), status: 'LIDA' } : item))
      );
    }
  };

  const handleOpenComunicado = (c: Comunicado) => {
    setSelectedComunicado(c);
    try {
      const raw = localStorage.getItem('ac_read_comunicados');
      const ids: string[] = raw ? JSON.parse(raw) : [];
      if (!ids.includes(c.id)) {
        const updated = [...ids, c.id];
        localStorage.setItem('ac_read_comunicados', JSON.stringify(updated));
      }
    } catch {}
    window.dispatchEvent(new CustomEvent('ac:comunicado:read', { detail: { id: c.id } }));
    api.post(`/comunicados/${c.id}/visualizar`).catch(() => {});
  };

  const formatDate = (d: any) => {
    if (!d) return '';
    try {
      const parsed = new Date(d);
      if (isNaN(parsed.getTime())) return '';
      return parsed.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: 'America/Sao_Paulo' });
    } catch {
      return '';
    }
  };

  const formatTime = (d: any) => {
    if (!d) return '';
    try {
      const parsed = new Date(d);
      if (isNaN(parsed.getTime())) return '';
      return parsed.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
    } catch {
      return '';
    }
  };

  const filteredComunicados = useMemo(() => {
    return comunicados.filter((c) => {
      const matchTipo = filterTipo === 'TODOS' || c.tipo === filterTipo;
      const matchSearch = !searchTerm || 
        c.titulo.toLowerCase().includes(searchTerm.toLowerCase()) || 
        c.corpo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.escola?.nome && c.escola.nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.municipio?.nome && c.municipio.nome.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchTipo && matchSearch;
    });
  }, [comunicados, filterTipo, searchTerm]);

  const naoLidasNotifs = notificacoes.filter((n) => !n.lidaEm).length;

  // ═══ DESKTOP VIEW ═══
  if (!isMobile) {
    const urgentes = comunicados.filter(c => c.tipo === 'URGENTE');

    return (
      <div className="admin-page" style={{ maxWidth: 1400, width: '100%', margin: '0 auto', paddingBottom: 32 }}>
        {/* ═══ Standardized Full-Width Hero Banner ═══ */}
        <div style={{
          background: 'radial-gradient(135% 140% at 100% 0%, #312e81 0%, #1e1b4b 55%, #0f172a 100%)',
          borderRadius: 20,
          padding: '20px 28px',
          color: '#ffffff',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ minWidth: 260, position: 'relative', zIndex: 2 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
              color: '#e0e7ff', marginBottom: 6,
            }}>
              <Megaphone size={12} color="#818cf8" /> Comunicação Oficial • Família & Escola
            </div>
            <h1 style={{ fontSize: '1.45rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em' }}>
              Mural de Comunicados & Avisos
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.72)' }}>
              Publicações timbradas oficiais da escola, comunicados da prefeitura e alertas ao vivo
            </p>
          </div>

          {/* Hero KPIs */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#38bdf8', lineHeight: 1.1 }}>{comunicados.length}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Mural</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#f87171', lineHeight: 1.1 }}>{urgentes.length}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Urgentes</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#f472b6', lineHeight: 1.1 }}>{notificacoes.length}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Alertas</div>
            </div>
          </div>
        </div>

        {/* ═══ 2-COLUMN BALANCED DESKTOP SPLIT LAYOUT ═══ */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, 0.75fr)',
          gap: 20,
          alignItems: 'start',
          width: '100%',
        }}>
          {/* ─── Coluna Esquerda: Feed Principal do Mural de Comunicados ─── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Toolbar com Filtros e Busca */}
            <div style={{
              background: '#ffffff',
              borderRadius: 16,
              padding: '12px 18px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              border: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12
            }}>
              {/* Filtro por Tipo */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {['TODOS', 'URGENTE', 'AVISO', 'EVENTO', 'REUNIAO', 'LEMBRETE'].map((tipo) => (
                  <button
                    key={tipo}
                    onClick={() => setFilterTipo(tipo)}
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: '1px solid',
                      borderColor: filterTipo === tipo ? '#2563eb' : '#e2e8f0',
                      background: filterTipo === tipo ? '#eff6ff' : '#ffffff',
                      color: filterTipo === tipo ? '#2563eb' : '#475569',
                      fontSize: '0.74rem', fontWeight: filterTipo === tipo ? 700 : 500,
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    {tipo === 'TODOS' ? 'Todos' : tipo}
                  </button>
                ))}
              </div>

              {/* Busca com fundo branco explícito */}
              <div style={{ position: 'relative', minWidth: 220, flex: 1 }}>
                <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Buscar comunicado..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%', padding: '7px 12px 7px 32px', borderRadius: 8,
                    border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a',
                    colorScheme: 'light', fontSize: '0.8rem', outline: 'none',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)', boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Lista de Publicações do Mural */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Carregando comunicados...</div>
            ) : filteredComunicados.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 20, border: '1px solid #e2e8f0' }}>
                <Megaphone size={40} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
                <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: '1rem', fontWeight: 700 }}>Nenhum comunicado encontrado</h3>
                <p style={{ color: '#64748b', fontSize: '0.82rem', margin: 0 }}>Nenhuma publicação corresponde aos filtros selecionados.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {filteredComunicados.map((c) => {
                  const theme = TIPO_COLORS[c.tipo] || { bg: '#f1f5f9', color: '#64748b' };
                  const midia = midiaUrlOf(c);
                  const origem = c.municipio?.nome ? `Secretaria Municipal de Educação de ${c.municipio.nome}` : c.escola?.nome || 'Comunicação Escolar';

                  return (
                    <div
                      key={c.id}
                      onClick={() => handleOpenComunicado(c)}
                      style={{
                        background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 20,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)', cursor: 'pointer', transition: 'all 0.2s',
                        display: 'flex', flexDirection: 'column', gap: 12, position: 'relative',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(37, 99, 235, 0.08)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.03)'; }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            fontSize: '0.68rem', fontWeight: 800, color: theme.color, background: theme.bg,
                            padding: '3px 8px', borderRadius: 6, letterSpacing: '0.3px', textTransform: 'uppercase',
                          }}>
                            {c.tipo}
                          </span>
                          {c.fixado && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.68rem', color: '#d97706', background: '#fef3c7', padding: '2px 8px', borderRadius: 6, fontWeight: 800 }}>
                              <Pin size={12} /> FIXADO
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: 500 }}>
                          {formatDate(c.criadoEm)}
                        </span>
                      </div>

                      {midia && (
                        <div style={{ borderRadius: 12, overflow: 'hidden', height: 160, background: '#f8fafc' }}>
                          <img src={midia} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      )}

                      <div>
                        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.35 }}>
                          {c.titulo}
                        </h3>
                        <p style={{
                          margin: '8px 0 0', fontSize: '0.82rem', color: '#475569', lineHeight: 1.5,
                          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {c.corpo}
                        </p>
                      </div>

                      <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.74rem', color: '#64748b' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%', fontWeight: 600 }}>
                          🏛️ {origem}
                        </span>
                        <span style={{ color: '#2563eb', fontWeight: 800 }}>Visualizar Comunicado Oficial →</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ─── Coluna Direita: Estatísticas & Filtros do Mural ─── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Card: Confirmação de Leitura da Família */}
            <div style={{
              background: '#ffffff',
              borderRadius: 20,
              border: '1px solid #e2e8f0',
              padding: 22,
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              display: 'flex',
              flexDirection: 'column',
              gap: 14
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ fontSize: '0.94rem', fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle2 size={18} color="#059669" /> Leituras & Confirmações
                </h4>
                <span style={{
                  fontSize: '0.7rem', fontWeight: 800,
                  color: percentLido === 100 ? '#059669' : '#2563eb',
                  background: percentLido === 100 ? '#dcfce7' : '#eff6ff',
                  padding: '3px 8px', borderRadius: 999
                }}>
                  {percentLido}% lidos
                </span>
              </div>

              {/* Progress Bar */}
              <div style={{ width: '100%', height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{
                  width: `${percentLido}%`,
                  height: '100%',
                  background: percentLido === 100 ? 'linear-gradient(90deg, #10b981, #059669)' : 'linear-gradient(90deg, #3b82f6, #2563eb)',
                  borderRadius: 999,
                  transition: 'width 0.4s ease',
                }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 2 }}>
                <div style={{ background: '#f8fafc', borderRadius: 12, padding: '10px 12px', border: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Visualizados</div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#059669', marginTop: 2 }}>{lidosCount}</div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: 12, padding: '10px 12px', border: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Pendentes</div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 800, color: pendentesCount > 0 ? '#d97706' : '#64748b', marginTop: 2 }}>{pendentesCount}</div>
                </div>
              </div>

              <p style={{ margin: 0, fontSize: '0.74rem', color: '#64748b', lineHeight: 1.4 }}>
                {pendentesCount === 0 && totalCount > 0
                  ? '✨ Parabéns! Você está em dia com todos os comunicados oficiais.'
                  : pendentesCount > 0
                  ? `📬 Você possui ${pendentesCount} publicação(ões) aguardando sua visualização.`
                  : 'Nenhum comunicado ativo no momento.'}
              </p>
            </div>

            {/* Card: Categorias e Distribuição das Publicações */}
            <div style={{
              background: '#ffffff',
              borderRadius: 20,
              border: '1px solid #e2e8f0',
              padding: 22,
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              display: 'flex',
              flexDirection: 'column',
              gap: 14
            }}>
              <h4 style={{ fontSize: '0.94rem', fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Filter size={16} color="#2563eb" /> Categorias do Mural
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { key: 'TODOS', label: 'Todas as Publicações', count: totalCount, color: '#2563eb', bg: '#eff6ff' },
                  { key: 'URGENTE', label: 'Urgentes & Avisos Importantes', count: contagemPorTipo.URGENTE || 0, color: '#dc2626', bg: '#fef2f2' },
                  { key: 'AVISO', label: 'Avisos Gerais', count: contagemPorTipo.AVISO || 0, color: '#d97706', bg: '#fffbeb' },
                  { key: 'EVENTO', label: 'Eventos & Calendário', count: contagemPorTipo.EVENTO || 0, color: '#7c3aed', bg: '#f5f3ff' },
                  { key: 'REUNIAO', label: 'Reuniões de Pais', count: contagemPorTipo.REUNIAO || 0, color: '#166534', bg: '#f0fdf4' },
                  { key: 'LEMBRETE', label: 'Lembretes Escolares', count: contagemPorTipo.LEMBRETE || 0, color: '#2563eb', bg: '#eff6ff' },
                ].map((item) => {
                  const isSelected = filterTipo === item.key;
                  return (
                    <button
                      key={item.key}
                      onClick={() => setFilterTipo(item.key)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '9px 12px',
                        borderRadius: 10,
                        border: isSelected ? `2px solid ${item.color}` : '1px solid #f1f5f9',
                        background: isSelected ? item.bg : '#ffffff',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ fontSize: '0.78rem', fontWeight: isSelected ? 800 : 600, color: isSelected ? item.color : '#334155' }}>
                        {item.label}
                      </span>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        color: isSelected ? '#ffffff' : '#64748b',
                        background: isSelected ? item.color : '#f1f5f9',
                        padding: '2px 7px',
                        borderRadius: 999,
                      }}>
                        {item.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Card: Canais de Atendimento & Contatos Oficiais */}
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: 20,
              border: '1px solid #e2e8f0',
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a', fontWeight: 800, fontSize: '0.86rem' }}>
                <School size={16} color="#2563eb" /> Canais Oficiais de Atendimento
              </div>
              <p style={{ fontSize: '0.74rem', color: '#64748b', margin: 0, lineHeight: 1.4 }}>
                Entre em contato direto com a secretaria, direção escolar ou coordenação pedagógica.
              </p>
              <button
                onClick={() => navigate('/responsavel/escola')}
                style={{
                  marginTop: 4,
                  background: '#ffffff',
                  color: '#2563eb',
                  border: '1px solid #cbd5e1',
                  borderRadius: 10,
                  padding: '9px 14px',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                Ver Contatos e Equipe Escolar
              </button>
            </div>
          </div>
        </div>

        <ComunicadoVisualizacaoModal
          open={!!selectedComunicado}
          onClose={() => setSelectedComunicado(null)}
          comunicadoInicial={selectedComunicado ? {
            id: selectedComunicado.id,
            titulo: selectedComunicado.titulo,
            corpo: selectedComunicado.corpo,
            mensagem: selectedComunicado.corpo,
            tipo: selectedComunicado.tipo,
            categoria: selectedComunicado.tipo,
            fixado: selectedComunicado.fixado,
            criadoEm: selectedComunicado.criadoEm,
            configPreview: selectedComunicado.configPreview,
            escola: selectedComunicado.escola,
            municipio: selectedComunicado.municipio,
          } : null}
          listaComunicados={comunicados.map((c) => ({
            id: c.id,
            titulo: c.titulo,
            corpo: c.corpo,
            mensagem: c.corpo,
            tipo: c.tipo,
            categoria: c.tipo,
            fixado: c.fixado,
            criadoEm: c.criadoEm,
            configPreview: c.configPreview,
            escola: c.escola,
            municipio: c.municipio,
          }))}
        />

        <NotificacaoDetalheSheet
          notificacao={selectedNotificacao}
          onClose={() => setSelectedNotificacao(null)}
        />
      </div>
    );
  }

  return (
    <div className="resp-mobile-root">
      <div className="resp-navy-header" style={{ padding: 'max(env(safe-area-inset-top, 40px), 40px) 20px 24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'white', letterSpacing: '-0.3px' }}>
            Avisos & Comunicados
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', fontWeight: 400 }}>
            Mural da escola e avisos dos alunos
          </p>
        </div>

        <div style={{
          display: 'flex', background: 'rgba(255,255,255,0.12)', borderRadius: 14,
          padding: 4, marginTop: 16, backdropFilter: 'blur(8px)', gap: 4
        }}>
          <button
            type="button"
            onClick={() => setActiveTab('mural')}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none',
              background: activeTab === 'mural' ? 'white' : 'transparent',
              color: activeTab === 'mural' ? '#1B365D' : 'rgba(255,255,255,0.85)',
              fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              boxShadow: activeTab === 'mural' ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
              transition: 'all 0.2s',
            }}
          >
            <Megaphone size={14} /> Mural ({comunicados.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('avisos')}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none',
              background: activeTab === 'avisos' ? 'white' : 'transparent',
              color: activeTab === 'avisos' ? '#1B365D' : 'rgba(255,255,255,0.85)',
              fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              boxShadow: activeTab === 'avisos' ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
              transition: 'all 0.2s',
            }}
          >
            <Bell size={14} /> Avisos {naoLidasNotifs > 0 && (
              <span style={{ background: '#ef4444', color: 'white', fontSize: '0.62rem', padding: '1px 5px', borderRadius: 10 }}>
                {naoLidasNotifs}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="resp-content-area" style={{ paddingBottom: 90 }}>
        {activeTab === 'mural' ? (
          loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>Carregando...</div>
          ) : comunicados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, background: 'white', borderRadius: 16, boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
              <Megaphone size={32} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
              <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0, fontWeight: 500 }}>Nenhum comunicado.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {comunicados.map(c => {
                const theme = TIPO_COLORS[c.tipo] || { bg: '#f1f5f9', color: '#64748b' };
                const origem = c.municipio?.nome ? `Prefeitura · ${c.municipio.nome}` : c.escola?.nome || '';
                return (
                  <div 
                    key={c.id} 
                    onClick={() => handleOpenComunicado(c)}
                    style={{
                      background: 'white', borderRadius: 16, padding: '16px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9',
                      position: 'relative', overflow: 'hidden', cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <span style={{ 
                        fontSize: '0.62rem', fontWeight: 700, color: theme.color, background: theme.bg, 
                        padding: '3px 8px', borderRadius: 6, letterSpacing: '0.5px' 
                      }}>
                        {c.tipo}
                      </span>
                      {c.fixado && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#f59e0b', fontSize: '0.68rem', fontWeight: 600 }}>
                          <Pin size={12} /> Fixado
                        </div>
                      )}
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 400 }}>
                        {formatDate(c.criadoEm)}
                      </span>
                    </div>
                    <h3 style={{ margin: '0 0 6px', fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.3 }}>
                      {c.titulo}
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontWeight: 400 }}>
                      {c.corpo}
                    </p>
                    {origem && (
                      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #f8fafc', fontSize: '0.68rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>🏛️ {origem}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          loadingNotifs ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>Carregando avisos...</div>
          ) : notificacoes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, background: 'white', borderRadius: 16, boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
              <Bell size={32} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
              <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0, fontWeight: 500 }}>Nenhum aviso registrado para os seus filhos.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {notificacoes.map((n) => (
                <div key={n.id} onClick={() => handleOpenNotificacao(n)} style={{
                  background: n.lidaEm ? '#ffffff' : '#f0fdf4',
                  borderRadius: 16, padding: '14px 16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  border: n.lidaEm ? '1px solid #f1f5f9' : '1px solid #bbf7d0',
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                  cursor: 'pointer'
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: n.lidaEm ? '#f1f5f9' : '#dcfce7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: n.lidaEm ? '#64748b' : '#16a34a', flexShrink: 0
                  }}>
                    <Bell size={16} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: n.lidaEm ? 500 : 700, color: '#0f172a' }}>
                        {n.titulo}
                      </span>
                      <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                        {formatTime(n.dataHora || n.criadoEm || n.createdAt)}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b', lineHeight: 1.3, fontWeight: 400 }}>
                      {n.mensagem}
                    </p>
                    {n.aluno?.nome && (
                      <div style={{ fontSize: '0.65rem', color: '#3b82f6', marginTop: 4, fontWeight: 500 }}>
                        Aluno: {n.aluno.nome}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <ComunicadoVisualizacaoModal
        open={!!selectedComunicado}
        onClose={() => setSelectedComunicado(null)}
        comunicadoInicial={selectedComunicado ? {
          id: selectedComunicado.id,
          titulo: selectedComunicado.titulo,
          corpo: selectedComunicado.corpo,
          mensagem: selectedComunicado.corpo,
          tipo: selectedComunicado.tipo,
          categoria: selectedComunicado.tipo,
          fixado: selectedComunicado.fixado,
          criadoEm: selectedComunicado.criadoEm,
          configPreview: selectedComunicado.configPreview,
          escola: selectedComunicado.escola,
          municipio: selectedComunicado.municipio,
        } : null}
        listaComunicados={comunicados.map((c) => ({
          id: c.id,
          titulo: c.titulo,
          corpo: c.corpo,
          mensagem: c.corpo,
          tipo: c.tipo,
          categoria: c.tipo,
          fixado: c.fixado,
          criadoEm: c.criadoEm,
          configPreview: c.configPreview,
          escola: c.escola,
          municipio: c.municipio,
        }))}
      />

      <NotificacaoDetalheSheet
        notificacao={selectedNotificacao}
        onClose={() => setSelectedNotificacao(null)}
      />

      <div className="resp-tab-bar">
        {[
          { key: 'home', icon: Home, label: 'Inicio', to: '/responsavel/dashboard' },
          { key: 'filhos', icon: Users, label: 'Filhos', to: '/responsavel/filhos' },
          { key: 'freq', icon: CalendarDays, label: 'Frequencia', to: '/responsavel/frequencia' },
          { key: 'avisos', icon: Bell, label: 'Avisos', to: '/responsavel/comunicados' },
          { key: 'perfil', icon: Settings, label: 'Perfil', to: '/responsavel/perfil' },
        ].map((tab) => (
          <button key={tab.key}
            className={`resp-tab-item ${tab.key === 'avisos' ? 'active' : ''}`}
            onClick={() => navigate(tab.to)}>
            <div style={{ position: 'relative' }}>
              <tab.icon size={20} />
            </div>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
